/**
 * The delivery badge's label, from the product service only:
 * `GET /v3/buyer/delivery-times?outletIds=…[&lat=…&lng=…]`.
 *
 * The server decides the text. A store's OWN time — set by GrabLocal in the CRM
 * (Outlet configuration), "Instant", "4–7 days" — is the badge for every buyer,
 * with or without a saved address: it is a promise about the store, not a
 * measurement from the buyer. A store without one gets the distance label
 * ("Instant" / "Next day" / "2-4 days" / "4-7 days") when the buyer's selected
 * coordinates are passed, and nothing without them. A store that delivers itself
 * gets no label at all: nobody has promised a speed.
 *
 * A storefront mounts the badge on dozens of cards, so every store asked for in
 * the same moment goes in ONE request (per position), and answers are kept for
 * the page.
 */

export type DeliveryTone = "fast" | "standard" | "blocked";

export interface OutletDelivery {
  /** Copy for the badge. Null when we have nothing honest to show. */
  label: string | null;
  tone: DeliveryTone;
  /** Straight-line distance from the product service — "0.8 km", "12 km". */
  distanceText: string | null;
  /** Outlet restricted and the selected address sits outside its area. */
  blocked: boolean;
}

/** One store's label as the product service answers it (`data.labels[outletId]`). */
export interface StoreDeliveryLabel {
  text: string;
  tone: "fast" | "standard";
  /** `store`: the store's own text. `auto`: the distance band. */
  source: "store" | "auto";
  distanceKm: number | null;
}

type Position = { lat: number; lng: number };

const UNKNOWN: OutletDelivery = { label: null, tone: "standard", distanceText: null, blocked: false };

/** "0.8 km" under 10 km; "12 km", "1,744 km" above. Null for no distance. */
export function formatDistanceKm(km: number | null | undefined): string | null {
  if (typeof km !== "number" || !Number.isFinite(km) || km < 0) return null;
  const tenths = Math.round(km * 10) / 10;
  return tenths < 10 ? `${Math.max(0.1, tenths).toFixed(1)} km` : `${Math.round(km).toLocaleString("en-IN")} km`;
}

/** What the badge says, from everything that can decide it. */
export function deliveryBadge({
  blocked,
  label,
}: {
  blocked: boolean;
  label: StoreDeliveryLabel | null;
}): OutletDelivery {
  // An unserviceable address outranks any speed claim: the buyer cannot get
  // this product at all.
  if (blocked) return { label: "Not delivered to your area", tone: "blocked", distanceText: null, blocked: true };

  if (!label?.text) return UNKNOWN;

  return {
    label: label.text,
    // "Instant" is the one promise worth an urgency colour; everything else is neutral.
    tone: label.tone === "fast" ? "fast" : "standard",
    distanceText: formatDistanceKm(label.distanceKm),
    blocked: false,
  };
}

/** The most stores one request may name (the server refuses more). */
const MAX_IDS_PER_REQUEST = 100;

/** How long to gather the stores the cards on screen ask for before one request. */
const BATCH_MS = 15;

/**
 * The buyer's selected position as sent: 3 decimals (~100 m). Delivery bands
 * start at 10 km, so finer is noise — and a coordinate that differs in the 6th
 * decimal must not miss the cache. Null when there is no usable position.
 */
export function positionKey(coords: Position | null | undefined): string | null {
  if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) return null;
  if (Math.abs(coords.lat) > 90 || Math.abs(coords.lng) > 180) return null;
  return `${coords.lat.toFixed(3)},${coords.lng.toFixed(3)}`;
}

const NO_POSITION = "";

interface Waiter {
  id: string;
  position: string;
  resolve: (label: StoreDeliveryLabel | null) => void;
}

/** One answer per (store, position) for the page. */
const answers = new Map<string, Promise<StoreDeliveryLabel | null>>();
let waiting: Waiter[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

const answerKey = (id: string, position: string) => `${position}|${id}`;

function baseUrl(): string | null {
  const url = process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL;
  return url ? url.replace(/\/+$/, "") : null;
}

const toneOf = (text: string): StoreDeliveryLabel["tone"] => (text === "Instant" ? "fast" : "standard");

/** `data.labels[id]` as sent, or null when it is absent or malformed. */
function readLabel(raw: unknown): StoreDeliveryLabel | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const text = typeof r.text === "string" ? r.text.trim() : "";
  if (!text) return null;
  const km = typeof r.distanceKm === "number" && Number.isFinite(r.distanceKm) && r.distanceKm >= 0 ? r.distanceKm : null;
  return {
    text,
    tone: r.tone === "fast" || r.tone === "standard" ? r.tone : toneOf(text),
    source: r.source === "auto" ? "auto" : "store",
    distanceKm: km,
  };
}

/**
 * Every label in one response. `data.labels` is the answer; an older server has
 * only `data.times` (the stores' own texts), which is read as a store label with
 * no distance — so the badge still shows the store's time, just not the band.
 */
function readLabels(body: unknown, ids: string[]): Map<string, StoreDeliveryLabel | null> {
  const data = (body as { data?: { labels?: unknown; times?: unknown } } | null)?.data;
  const labels = data?.labels && typeof data.labels === "object" ? (data.labels as Record<string, unknown>) : {};
  const times = data?.times && typeof data.times === "object" ? (data.times as Record<string, unknown>) : {};

  const out = new Map<string, StoreDeliveryLabel | null>();
  for (const id of ids) {
    const own = typeof times[id] === "string" ? (times[id] as string).trim() : "";
    out.set(
      id,
      readLabel(labels[id]) ?? (own ? { text: own, tone: toneOf(own), source: "store", distanceKm: null } : null)
    );
  }
  return out;
}

/** One request for up to MAX_IDS_PER_REQUEST stores at one position. Null: it failed. */
async function fetchLabels(ids: string[], position: string): Promise<Map<string, StoreDeliveryLabel | null> | null> {
  try {
    const [lat, lng] = position ? position.split(",") : [];
    const where = lat && lng ? `&lat=${lat}&lng=${lng}` : "";
    const res = await fetch(`${baseUrl()}/v3/buyer/delivery-times?outletIds=${encodeURIComponent(ids.join(","))}${where}`);
    if (!res.ok) return null;
    return readLabels(await res.json(), ids);
  } catch {
    return null;
  }
}

async function flush(): Promise<void> {
  const batch = waiting;
  waiting = [];
  timer = null;

  const byPosition = new Map<string, Waiter[]>();
  for (const w of batch) byPosition.set(w.position, [...(byPosition.get(w.position) ?? []), w]);

  await Promise.all(
    [...byPosition].map(async ([position, group]) => {
      const ids = [...new Set(group.map((w) => w.id))];
      const found = new Map<string, StoreDeliveryLabel | null>();
      const failed = new Set<string>();

      for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
        const chunk = ids.slice(i, i + MAX_IDS_PER_REQUEST);
        const labels = await fetchLabels(chunk, position);
        for (const id of chunk) {
          if (labels) found.set(id, labels.get(id) ?? null);
          else failed.add(id);
        }
      }

      for (const w of group) {
        // A failed request is not "no label": the next ask may try again.
        if (failed.has(w.id)) answers.delete(answerKey(w.id, w.position));
        w.resolve(found.get(w.id) ?? null);
      }
    })
  );
}

/**
 * This store's delivery label for the buyer at `coords` (their selected
 * address), or null: no label applies (a store that delivers itself, no position
 * for a store without its own time), or the request failed.
 */
export function getStoreDeliveryLabel(
  outletId: string,
  coords?: Position | null
): Promise<StoreDeliveryLabel | null> {
  if (!outletId || !baseUrl()) return Promise.resolve(null);

  const position = positionKey(coords) ?? NO_POSITION;
  const key = answerKey(outletId, position);
  const known = answers.get(key);
  if (known) return known;

  const answer = new Promise<StoreDeliveryLabel | null>((resolve) => {
    waiting.push({ id: outletId, position, resolve });
    if (!timer) timer = setTimeout(() => void flush(), BATCH_MS);
  });
  answers.set(key, answer);

  return answer;
}

/** Tests only: forget every answer. */
export function resetStoreDeliveryTimes(): void {
  answers.clear();
  waiting = [];
  if (timer) clearTimeout(timer);
  timer = null;
}
